class VoiceSearch extends HTMLElement {
	constructor() {
		super();
		this.recognition = null;
		this.isListening = false;
		this.micButton = null;
		this.searchInput = null;
		this.silenceTimeout = null;
		this.SILENCE_TIMEOUT = 5000; // 5 secondi di silenzio prima di interrompere
		
		this.init();
	}

	init() {
		// Verifica se il browser supporta il riconoscimento vocale
		const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
		
		if (!SpeechRecognition) {
			// Nascondi il pulsante se il browser non supporta il riconoscimento vocale
			if (this.micButton) {
				this.micButton.style.display = 'none';
			}
			return;
		}

		this.micButton = this.querySelector('.voice-search__button');
		this.searchInput = this.closest('form')?.querySelector('input[type="search"]');
		
		if (!this.micButton || !this.searchInput) {
			return;
		}

		// Inizializza il riconoscimento vocale
		this.recognition = new SpeechRecognition();
		this.recognition.continuous = false;
		this.recognition.interimResults = false;
		this.recognition.lang = document.documentElement.lang || 'it-IT';

		// Event listeners
		this.micButton.addEventListener('click', this.toggleRecognition.bind(this));
		
		this.recognition.onstart = () => {
			this.isListening = true;
			this.micButton.classList.add('voice-search__button--listening');
			this.micButton.setAttribute('aria-label', 'Ascolto in corso... Clicca per interrompere');
			
			// Avvia il timeout per il silenzio
			this.startSilenceTimeout();
		};

		this.recognition.onresult = (event) => {
			// Clear del timeout quando c'è un risultato
			this.clearSilenceTimeout();
			
			const transcript = event.results[0][0].transcript;
			if (transcript && transcript.trim()) {
				this.searchInput.value = transcript.trim();
				
				// Trigger input event per attivare la ricerca predittiva se presente
				this.searchInput.dispatchEvent(new Event('input', { bubbles: true }));
				
				// Submit automatico del form dopo un breve delay per permettere l'aggiornamento dell'input
				setTimeout(() => {
					const form = this.searchInput.closest('form');
					if (form) {
						form.submit();
					}
				}, 100);
			}
			
			// Interrompi l'ascolto dopo aver ricevuto il risultato
			this.stopRecognition();
		};

		this.recognition.onerror = (event) => {
			console.error('Errore riconoscimento vocale:', event.error);
			this.clearSilenceTimeout();
			
			// Gestisci errori specifici
			if (event.error === 'no-speech') {
				// Se non c'è parlato, il timeout del silenzio gestirà l'interruzione
				// Non fermiamo qui, lasciamo che il timeout faccia il suo lavoro
				return;
			}
			
			// Per altri errori, interrompi l'ascolto
			if (event.error !== 'aborted') {
				this.stopRecognition();
				
				// Mostra un messaggio all'utente
				if (event.error === 'not-allowed') {
					this.micButton.setAttribute('aria-label', 'Permesso microfono negato.');
				} else {
					this.micButton.setAttribute('aria-label', 'Errore nel riconoscimento vocale.');
				}
				
				setTimeout(() => {
					this.micButton.setAttribute('aria-label', 'Cerca con la voce');
				}, 3000);
			}
		};

		this.recognition.onend = () => {
			this.clearSilenceTimeout();
			// Se l'ascolto è terminato naturalmente (non per nostro stop), ferma lo stato
			if (this.isListening) {
				// Se siamo qui senza risultato, significa che è finito per timeout o silenzio
				this.stopRecognition();
			}
		};
	}

	toggleRecognition(event) {
		event.preventDefault();
		event.stopPropagation();

		if (this.isListening) {
			this.stopRecognition();
		} else {
			this.startRecognition();
		}
	}

	startRecognition() {
		if (this.recognition && !this.isListening) {
			try {
				this.recognition.start();
			} catch (error) {
				console.error('Errore nell\'avvio del riconoscimento:', error);
			}
		}
	}

	stopRecognition() {
		if (this.recognition && this.isListening) {
			try {
				this.recognition.stop();
			} catch (error) {
				// Ignora errori durante lo stop
			}
			this.clearSilenceTimeout();
			this.isListening = false;
			this.micButton.classList.remove('voice-search__button--listening');
			this.micButton.setAttribute('aria-label', 'Cerca con la voce');
		}
	}

	startSilenceTimeout() {
		this.clearSilenceTimeout();
		this.silenceTimeout = setTimeout(() => {
			if (this.isListening) {
				this.stopRecognition();
				this.micButton.setAttribute('aria-label', 'Ascolto interrotto per silenzio');
				setTimeout(() => {
					this.micButton.setAttribute('aria-label', 'Cerca con la voce');
				}, 2000);
			}
		}, this.SILENCE_TIMEOUT);
	}

	resetSilenceTimeout() {
		if (this.isListening) {
			this.startSilenceTimeout();
		}
	}

	clearSilenceTimeout() {
		if (this.silenceTimeout) {
			clearTimeout(this.silenceTimeout);
			this.silenceTimeout = null;
		}
	}
}

customElements.define('voice-search', VoiceSearch);
